import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  Play, Pause, RotateCcw, Trash2, RefreshCw, 
  CheckCircle, XCircle, Clock, Loader2, AlertCircle, Database, Zap, Leaf
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SpecCollectorQueueWidget = ({ onTaskComplete }) => {
  const [queueStatus, setQueueStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [collectorMode, setCollectorMode] = useState(null);
  const [modeLoading, setModeLoading] = useState(false);

  const fetchQueueStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/spec-collection-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueueStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCollectorMode = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/spec-collector/mode`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollectorMode(response.data);
    } catch (error) {
      console.error('Failed to fetch collector mode:', error);
    }
  }, []);

  useEffect(() => {
    fetchQueueStatus();
    fetchCollectorMode();
    
    // Auto-refresh every 10 seconds if processing
    const interval = setInterval(() => {
      fetchQueueStatus();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchQueueStatus, fetchCollectorMode]);

  const handleModeToggle = async () => {
    setModeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const newDevMode = !collectorMode?.dev_mode;
      
      await axios.post(`${API}/admin/spec-collector/mode`, 
        { dev_mode: newDevMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchCollectorMode();
      
      if (newDevMode) {
        toast.success('DEV-Modus aktiviert (Credit-Sparend)', {
          description: 'Nur OpenAI wird verwendet. Server-Neustart für permanente Änderung nötig.'
        });
      } else {
        toast.success('PROD-Modus aktiviert (Volle Power)', {
          description: 'Gemini + GPT-4o + Claude werden verwendet. Server-Neustart für permanente Änderung nötig.'
        });
      }
    } catch (error) {
      toast.error('Modus konnte nicht geändert werden');
    } finally {
      setModeLoading(false);
    }
  };

  const handleQueueAction = async (action) => {
    setActionLoading(action);
    try {
      const token = localStorage.getItem('token');
      let response;
      
      switch (action) {
        case 'start':
          response = await axios.post(`${API}/admin/spec-collection-queue/start`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Queue gestartet');
          break;
        case 'pause':
          response = await axios.post(`${API}/admin/spec-collection-queue/pause`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Queue pausiert: ${response.data.paused_count} Tasks pausiert`);
          break;
        case 'resume':
          response = await axios.post(`${API}/admin/spec-collection-queue/resume`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Queue fortgesetzt: ${response.data.resumed_count} Tasks fortgesetzt`);
          break;
        case 'clear':
          if (!window.confirm('Wirklich alle Tasks aus der Queue löschen?')) return;
          response = await axios.delete(`${API}/admin/spec-collection-queue/clear`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Queue geleert: ${response.data.deleted_count} Tasks gelöscht`);
          break;
        default:
          break;
      }
      
      // Refresh status
      setTimeout(fetchQueueStatus, 500);
    } catch (error) {
      toast.error(`Aktion fehlgeschlagen: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Wartend</Badge>;
      case 'processing':
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20 animate-pulse">Verarbeitet...</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Abgeschlossen</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">Fehlgeschlagen</Badge>;
      case 'paused':
        return <Badge variant="outline" className="bg-gray-500/10 text-gray-400 border-gray-500/20">Pausiert</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2" style={{ color: '#94A3B8' }}>Lade Queue-Status...</span>
        </CardContent>
      </Card>
    );
  }

  if (!queueStatus) {
    return null;
  }

  const { is_processing, is_paused, current_task, next_task, counts, recent_tasks, settings } = queueStatus;

  return (
    <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
            <Database className="w-5 h-5" />
            Spec Collector Queue
          </CardTitle>
          <div className="flex items-center gap-2">
            {is_processing && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 animate-pulse">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Läuft
              </Badge>
            )}
            {is_paused && (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                <Pause className="w-3 h-3 mr-1" />
                Pausiert
              </Badge>
            )}
            {!is_processing && !is_paused && counts?.pending > 0 && (
              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                Gestoppt
              </Badge>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchQueueStatus}
              style={{ color: '#94A3B8' }}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Task */}
        {current_task && (
          <div 
            className="p-4 rounded-lg border animate-pulse"
            style={{ backgroundColor: '#1EA0E620', borderColor: '#1EA0E650' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
              <span className="font-semibold" style={{ color: '#7CD4FD' }}>
                Aktuell: {current_task.brand} {current_task.model}
              </span>
            </div>
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              Gestartet: {new Date(current_task.started_at).toLocaleTimeString('de-DE')}
              {current_task.engine && ` • Motor: ${current_task.engine}`}
            </p>
          </div>
        )}

        {/* Queue Stats */}
        <div className="grid grid-cols-5 gap-2">
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="text-2xl font-bold" style={{ color: '#EAB308' }}>{counts?.pending || 0}</div>
            <div className="text-xs" style={{ color: '#94A3B8' }}>Wartend</div>
          </div>
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="text-2xl font-bold" style={{ color: '#3B82F6' }}>{counts?.processing || 0}</div>
            <div className="text-xs" style={{ color: '#94A3B8' }}>In Arbeit</div>
          </div>
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="text-2xl font-bold" style={{ color: '#22C55E' }}>{counts?.completed || 0}</div>
            <div className="text-xs" style={{ color: '#94A3B8' }}>Fertig</div>
          </div>
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="text-2xl font-bold" style={{ color: '#EF4444' }}>{counts?.failed || 0}</div>
            <div className="text-xs" style={{ color: '#94A3B8' }}>Fehler</div>
          </div>
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="text-2xl font-bold" style={{ color: '#6B7280' }}>{counts?.paused || 0}</div>
            <div className="text-xs" style={{ color: '#94A3B8' }}>Pausiert</div>
          </div>
        </div>

        {/* Next Task Preview */}
        {next_task && !current_task && (
          <div 
            className="p-3 rounded-lg border"
            style={{ backgroundColor: '#1F293740', borderColor: '#1F2937' }}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" style={{ color: '#94A3B8' }} />
              <span style={{ color: '#94A3B8' }}>
                Nächster: <span style={{ color: '#E6EDF3' }}>{next_task.brand} {next_task.model}</span>
              </span>
            </div>
          </div>
        )}

        {/* Control Buttons */}
        <div className="flex flex-wrap gap-2">
          {!is_processing && !is_paused && counts?.pending > 0 && (
            <Button
              size="sm"
              onClick={() => handleQueueAction('start')}
              disabled={actionLoading === 'start'}
              style={{ backgroundColor: '#22C55E', color: '#fff' }}
            >
              {actionLoading === 'start' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Starten
            </Button>
          )}
          
          {is_processing && (
            <Button
              size="sm"
              onClick={() => handleQueueAction('pause')}
              disabled={actionLoading === 'pause'}
              variant="outline"
              style={{ borderColor: '#EAB308', color: '#EAB308' }}
            >
              {actionLoading === 'pause' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Pause className="w-4 h-4 mr-2" />
              )}
              Pausieren
            </Button>
          )}
          
          {is_paused && counts?.paused > 0 && (
            <Button
              size="sm"
              onClick={() => handleQueueAction('resume')}
              disabled={actionLoading === 'resume'}
              style={{ backgroundColor: '#3B82F6', color: '#fff' }}
            >
              {actionLoading === 'resume' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Fortsetzen
            </Button>
          )}
          
          {(counts?.pending > 0 || counts?.paused > 0 || counts?.completed > 0 || counts?.failed > 0) && (
            <Button
              size="sm"
              onClick={() => handleQueueAction('clear')}
              disabled={actionLoading === 'clear'}
              variant="outline"
              style={{ borderColor: '#EF4444', color: '#EF4444' }}
            >
              {actionLoading === 'clear' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Queue leeren
            </Button>
          )}
        </div>

        {/* Recent Tasks */}
        {recent_tasks && recent_tasks.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold mb-2" style={{ color: '#94A3B8' }}>
              Letzte Tasks
            </h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {recent_tasks.slice(0, 5).map((task, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-2 rounded text-sm"
                  style={{ backgroundColor: '#1F2937' }}
                >
                  <span style={{ color: '#E6EDF3' }}>
                    {task.brand} {task.model}
                  </span>
                  {getStatusBadge(task.status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Mode Switch - Always show even if mode not loaded yet */}
        <div className="pt-4 border-t" style={{ borderColor: '#1F2937' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {(collectorMode?.dev_mode ?? true) ? (
                <Leaf className="w-4 h-4" style={{ color: '#22C55E' }} />
              ) : (
                <Zap className="w-4 h-4" style={{ color: '#F59E0B' }} />
              )}
              <div>
                <p className="text-sm font-medium" style={{ color: '#E6EDF3' }}>
                  KI-Modus: {collectorMode ? (collectorMode.dev_mode ? 'DEV (Credit-Sparend)' : 'PROD (Volle Power)') : 'Lädt...'}
                </p>
                <p className="text-xs" style={{ color: '#6B7280' }}>
                  {(collectorMode?.dev_mode ?? true)
                    ? 'Nur OpenAI direkt - spart Emergent Credits' 
                    : 'Gemini + GPT-4o + Claude - volle KI-Power'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: (collectorMode?.dev_mode ?? true) ? '#22C55E' : '#6B7280' }}>DEV</span>
              <Switch
                checked={collectorMode ? !collectorMode.dev_mode : false}
                onCheckedChange={handleModeToggle}
                disabled={modeLoading || !collectorMode}
              />
              <span className="text-xs" style={{ color: (collectorMode && !collectorMode.dev_mode) ? '#F59E0B' : '#6B7280' }}>PROD</span>
            </div>
          </div>
          {modeLoading && (
            <p className="text-xs mt-2 flex items-center gap-1" style={{ color: '#94A3B8' }}>
              <Loader2 className="w-3 h-3 animate-spin" />
              Modus wird geändert...
            </p>
          )}
        </div>

        {/* Settings Info */}
        <div className="pt-2 border-t" style={{ borderColor: '#1F2937' }}>
          <p className="text-xs" style={{ color: '#6B7280' }}>
            ⚙️ Intervall: {settings?.interval_seconds || 60}s zwischen Tasks • 
            Max. {settings?.max_concurrent || 1} gleichzeitig
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SpecCollectorQueueWidget;
